import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import ComprovanteLayout from "./comprovanteLayout";
import ComprovanteTable from "./comprovanteTable";
import EditComprovanteModal from "./editComprovanteModal";
import DeleteComprovanteModal from "./deleteComprovanteModal";

/**
 * Componente principal que gerencia a visualização, edição e remoção de comprovantes.
 */
function Comprovante() {
    const [authenticated, setAuthenticated] = useState(false);
    const [clientes, setClientes] = useState([]);
    const [usuario, setUsuario] = useState(null);
    const [processos, setProcessos] = useState([]);
    const [comprovantes, setComprovantes] = useState([]);
    const [comprovanteSelecionado, setComprovanteSelecionado] = useState(null);
    const [modalEditOpen, setModalEditOpen] = useState(false);
    const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Autenticação e obtenção de dados dos clientes e usuário
        axios
            .post("/api/get/client", { withCredentials: true })
            .then((response) => {
                if (response.data.auth) {
                    setAuthenticated(true);
                    setClientes(response.data.clientes);
                    setUsuario(response.data.usuario);
                    if (response.data.nivel !== "Aners") {
                        // Usuário não administrador
                        // Pode adicionar lógica específica para clientes aqui
                    }
                } else {
                    navigate("/login");
                }
            })
            .catch(() => {
                navigate("/login");
            });

        // Obtenção de processos
        axios
            .post("/api/getprocess", { withCredentials: true })
            .then((response) => {
                if (response.data.auth) {
                    setProcessos(response.data.resp);
                } else {
                    navigate("/login");
                }
            })
            .catch(() => {
                navigate("/login");
            });
    }, [navigate]);

    /**
     * Busca os nomes dos comprovantes com base no processo selecionado.
     */
    const getNameReceipt = () => {
        const processo = document.querySelector("#processo").value;
        axios
            .post(
                "/api/get/nameReceipt/",
                { processo },
                { withCredentials: true }
            )
            .then((resp) => {
                setComprovantes(resp.data.pagamento);
            })
            .catch(() => {
                alert("Erro ao buscar os nomes dos comprovantes.");
            });
    };

    /**
     * Formata o campo "Processo" conforme o padrão (000000-000).
     * @param {Object} e - Evento de input.
     */
    const formatProcesso = (e) => {
        const campo = e.target;
        const valor = campo.value.replace(/\D/g, ""); // Remove tudo o que não é dígito
        const regex = /^(\d{0,6})-?(\d{0,3})?$/;
        const partes = regex.exec(valor);
        if (partes !== null) {
            const novoValor = partes[1] + (partes[2] ? "-" + partes[2] : "");
            campo.value = novoValor;
        } else {
            campo.value = campo.value.slice(0, -1);
        }
    };

    /**
     * Remove a tabela existente para evitar duplicações.
     */
    const deleteTable = () => {
        setComprovantes([]);
    };

    /**
     * Busca e exibe os comprovantes com base nos filtros selecionados.
     */
    const getReceipt = () => {
        const cliente = document.querySelector("#cliente").value;
        const processo = document.querySelector("#processo").value;
        const comprovante = document.querySelector("#comprovante").value;

        axios
            .post(
                "/api/get/Receipt/",
                { cliente, processo, comprovante },
                { withCredentials: true }
            )
            .then((resp) => {
                setComprovantes(resp.data.comprovante);
            })
            .catch(() => {
                alert("Erro ao buscar os comprovantes.");
            });
    };

    /**
     * Abre o modal de edição com o comprovante selecionado.
     * @param {number|string} id - ID do comprovante.
     */
    const handleEdit = (id) => {
        setComprovanteSelecionado(id);
        setModalEditOpen(true);
    };

    /**
     * Abre o modal de remoção com o comprovante selecionado.
     * @param {number|string} id - ID do comprovante.
     */
    const handleDelete = (id) => {
        setComprovanteSelecionado(id);
        setModalDeleteOpen(true);
    };

    /**
     * Atualiza o comprovante após edição.
     * @param {number|string} id - ID do comprovante.
     * @param {Object} dados - Dados atualizados do comprovante.
     */
    const updateComprovante = (id, dados) => {
        setComprovantes(
            comprovantes.map((comprovante) =>
                comprovante.id === id
                    ? { ...comprovante, ...dados }
                    : comprovante
            )
        );
    };

    /**
     * Remove o comprovante do estado após confirmação.
     * @param {number|string} id - ID do comprovante.
     */
    const removeComprovante = (id) => {
        setComprovantes(
            comprovantes.filter((comprovante) => comprovante.id !== id)
        );
    };

    if (!authenticated) {
        // Exibe um spinner de carregamento enquanto verifica a autenticação
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <ComprovanteLayout>
                <div className="row">
                    <h1>Pesquisar Comprovantes</h1>
                </div>
                <div className="row">
                    <div className="col-lg-4 col-md-4 col-sm-12">
                        <div className="form-group">
                            <label htmlFor="cliente">Cliente</label>
                            <select
                                id="cliente"
                                className="form-control"
                                name="cliente"
                                disabled={false} // Pode ajustar conforme a lógica necessária
                            >
                                {clientes.map((cliente) => (
                                    <option key={cliente.id}>
                                        {cliente.nome}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-4 col-sm-12">
                        <div className="form-group">
                            <label htmlFor="processo">Processo</label>
                            <input
                                type="text"
                                className="form-control"
                                id="processo"
                                name="processo"
                                onInput={formatProcesso}
                                onChange={getNameReceipt}
                                placeholder="000000-000"
                            />
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-4 col-sm-12">
                        <div className="form-group">
                            <label htmlFor="comprovante">Comprovante(s)</label>
                            <select
                                id="comprovante"
                                className="form-control"
                                name="comprovante"
                            >
                                <option>Todos</option>
                                {comprovantes.length > 0
                                    ? comprovantes.map((comprovante, index) => (
                                          <option key={index}>
                                              {comprovante.anexo}
                                          </option>
                                      ))
                                    : null}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="row text-center">
                    <button
                        className="btn btn-outline-info"
                        type="button"
                        onClick={async () => {
                            deleteTable();
                            await getReceipt();
                        }}
                    >
                        Consultar <i className="fa fa-search" />
                    </button>
                </div>
                <div className="row mt-4">
                    <div className="col-12">
                        <ComprovanteTable
                            comprovantes={comprovantes}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    </div>
                </div>
            </ComprovanteLayout>

            {/* Modal de Edição */}
            <EditComprovanteModal
                open={modalEditOpen}
                handleClose={() => setModalEditOpen(false)}
                handleSave={updateComprovante}
                comprovanteId={comprovanteSelecionado}
            />

            {/* Modal de Remoção */}
            <DeleteComprovanteModal
                open={modalDeleteOpen}
                handleClose={() => setModalDeleteOpen(false)}
                handleDelete={removeComprovante}
                comprovanteId={comprovanteSelecionado}
            />
        </>
    );
}

export default Comprovante;
